import { flyerFont } from '@pulsify/fonts'
import {
  type LogoPlacement,
  type PhotoFont,
  type PhotoLayout,
  type PhotoTextEffect,
  photoFontValues,
} from '@pulsify/shared/dto'
import { type PointerEvent, type ReactNode, useEffect, useId, useRef } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { ensureVendoredFontsLoaded } from '~/shared/lib/vendored-fonts'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Chip } from './chat-ui'

const EFFECTS: { value: PhotoTextEffect; label: () => string }[] = [
  { value: 'none', label: () => m.aiFlowPhotoEffectNone() },
  { value: 'shadow', label: () => m.aiFlowPhotoEffectShadow() },
  { value: 'gradient', label: () => m.aiFlowPhotoEffectGradient() },
]

const POSITIONS: { y: number; label: () => string }[] = [
  { y: 0.18, label: () => m.aiFlowPhotoTop() },
  { y: 0.5, label: () => m.aiFlowPhotoMiddle() },
  { y: 0.82, label: () => m.aiFlowPhotoBottom() },
]

const LOGOS: { value: LogoPlacement; label: () => string }[] = [
  { value: 'top-right', label: () => m.aiFlowLogoTopRight() },
  { value: 'bottom', label: () => m.aiFlowLogoBottom() },
  { value: 'none', label: () => m.aiFlowLogoNone() },
]

const WHITE = '#ffffff'
const BLACK = '#000000'

interface PhotoEditorProps {
  photoUrl: string
  layout: PhotoLayout
  onChange: (layout: PhotoLayout) => void
  brandColors: string[]
  logoUrl: string | null
}

/**
 * Live preview of the text on the photo. It only previews: the server sets the final text
 * with the same font files, at the same relative position and size.
 */
export function PhotoEditor({
  photoUrl,
  layout,
  onChange,
  brandColors,
  logoUrl,
}: PhotoEditorProps) {
  const formId = useId()
  const frameRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const set = (patch: Partial<PhotoLayout>) => onChange({ ...layout, ...patch })

  useEffect(() => {
    ensureVendoredFontsLoaded()
  }, [])

  const moveTo = (event: PointerEvent<HTMLDivElement>) => {
    const rect = frameRef.current?.getBoundingClientRect()
    if (!rect) return
    const clamp = (value: number) => Math.min(0.95, Math.max(0.05, value))
    set({
      x: clamp((event.clientX - rect.left) / rect.width),
      y: clamp((event.clientY - rect.top) / rect.height),
    })
  }

  const colors = [...new Set([...brandColors, WHITE, BLACK])]
  const gradientDown = layout.y >= 0.5

  return (
    <div className="mt-3 flex flex-col gap-4">
      <div
        ref={frameRef}
        role="application"
        aria-label={m.aiFlowPhotoDragHint()}
        className="relative aspect-[3/2] w-full touch-none select-none overflow-hidden rounded-xl bg-muted [container-type:size]"
        onPointerDown={(event) => {
          draggingRef.current = true
          event.currentTarget.setPointerCapture(event.pointerId)
          moveTo(event)
        }}
        onPointerMove={(event) => draggingRef.current && moveTo(event)}
        onPointerUp={() => {
          draggingRef.current = false
        }}
      >
        <img src={photoUrl} alt="" className="absolute inset-0 size-full object-cover" />
        {layout.effect === 'gradient' && (
          <div
            className={cn(
              'absolute inset-0 from-45% from-transparent to-black/70',
              gradientDown ? 'bg-gradient-to-b' : 'bg-gradient-to-t',
            )}
          />
        )}
        {logoUrl && layout.logo !== 'none' && (
          <img
            src={logoUrl}
            alt=""
            className={cn(
              'absolute object-contain',
              layout.logo === 'top-right'
                ? 'top-[5cqmin] right-[5cqmin] size-[18cqmin]'
                : 'bottom-[5cqmin] left-1/2 size-[12cqmin] -translate-x-1/2',
            )}
          />
        )}
        <div
          className="pointer-events-none absolute w-[90%] -translate-x-1/2 -translate-y-1/2 text-center leading-tight"
          style={{
            left: `${layout.x * 100}%`,
            top: `${layout.y * 100}%`,
            color: layout.color,
            fontFamily: `"${flyerFont(layout.font).family}"`,
            textShadow:
              layout.effect === 'shadow' ? '0 0.04em 0.12em rgb(0 0 0 / 0.75)' : undefined,
          }}
        >
          {layout.title && (
            <div className="font-bold" style={{ fontSize: `${layout.size * 100}cqmin` }}>
              {layout.title}
            </div>
          )}
          {layout.subtitle && (
            <div style={{ fontSize: `${layout.size * 45}cqmin` }}>{layout.subtitle}</div>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${formId}-title`}>{m.aiFlowPhotoTitle()}</Label>
          <Input
            id={`${formId}-title`}
            value={layout.title}
            maxLength={120}
            onChange={(event) => set({ title: event.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${formId}-subtitle`}>{m.aiFlowPhotoSubtitle()}</Label>
          <Input
            id={`${formId}-subtitle`}
            value={layout.subtitle}
            maxLength={120}
            onChange={(event) => set({ subtitle: event.target.value })}
          />
        </div>
      </div>

      <EditorRow label={m.aiFlowPhotoFont()}>
        {photoFontValues.map((font: PhotoFont) => (
          <Chip
            key={font}
            aria-pressed={layout.font === font}
            className={cn(layout.font === font && 'border-brand')}
            style={{ fontFamily: `"${flyerFont(font).family}"` }}
            onClick={() => set({ font })}
          >
            {flyerFont(font).family}
          </Chip>
        ))}
      </EditorRow>

      <EditorRow label={m.aiFlowPhotoSize()}>
        <input
          type="range"
          min={0.04}
          max={0.2}
          step={0.005}
          value={layout.size}
          aria-label={m.aiFlowPhotoSize()}
          className="w-48 accent-[var(--brand)]"
          onChange={(event) => set({ size: Number(event.target.value) })}
        />
      </EditorRow>

      <EditorRow label={m.aiFlowPhotoPosition()}>
        {POSITIONS.map((position) => (
          <Chip
            key={position.y}
            aria-pressed={Math.abs(layout.y - position.y) < 0.01}
            onClick={() => set({ x: 0.5, y: position.y })}
          >
            {position.label()}
          </Chip>
        ))}
      </EditorRow>

      <EditorRow label={m.aiFlowPhotoColor()}>
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            aria-label={color}
            aria-pressed={layout.color.toLowerCase() === color.toLowerCase()}
            onClick={() => set({ color })}
            className={cn(
              'size-7 rounded-full ring-1 ring-border outline-none focus-visible:ring-2 focus-visible:ring-ring',
              layout.color.toLowerCase() === color.toLowerCase() &&
                'ring-2 ring-brand ring-offset-2 ring-offset-card',
            )}
            style={{ background: color }}
          />
        ))}
      </EditorRow>

      <EditorRow label={m.aiFlowPhotoEffect()}>
        {EFFECTS.map((effect) => (
          <Chip
            key={effect.value}
            aria-pressed={layout.effect === effect.value}
            className={cn(layout.effect === effect.value && 'border-brand')}
            onClick={() => set({ effect: effect.value })}
          >
            {effect.label()}
          </Chip>
        ))}
      </EditorRow>

      {logoUrl && (
        <EditorRow label={m.aiFlowPhotoLogo()}>
          {LOGOS.map((logo) => (
            <Chip
              key={logo.value}
              aria-pressed={layout.logo === logo.value}
              className={cn(layout.logo === logo.value && 'border-brand')}
              onClick={() => set({ logo: logo.value })}
            >
              {logo.label()}
            </Chip>
          ))}
        </EditorRow>
      )}
    </div>
  )
}

function EditorRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
      <span className="w-28 flex-none text-muted-foreground text-sm">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  )
}
